import {
  Badge,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTitle,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Table,
} from 'reactstrap';
import { ProperPreSaleVisit } from '../models/dashboardModels';
import {
  IconCalendar,
  IconCheck,
  IconClock,
  IconDots,
  IconMail,
  IconMapPin,
  IconPhone,
  IconTrash,
} from '@tabler/icons-react';
import {
  getBadgeColorVisitPreSaleStatusAdapter,
  getVisitPreSaleStatusAdapter,
} from '../../properties/adapters/visitsAdapter';
import { VisitPreSaleState } from '../../../constants/visitsConstants';
import { useCallback, useState } from 'react';
import VisitMarketplaceTypeBadge from '../../../components/VisitMarketplaceTypeBadge';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAuth from '../../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import {
  deleteVisitPreSaleEndpoint,
  markAsDoneVisitPreSaleEndpoint,
} from '../services/dashboardService';
import DeleteVisitModal from '../../investors/components/DeleteVisitModal';
import { getCancellationReasons } from '../../properties/services/propertyService';
import InvestorVisitsBadge from '../../../components/badges/InvestorVisitsBadge';

export interface Props {
  visits: ProperPreSaleVisit[];
  footer?: any;
}
const DashboardPreSaleVisitListTable = ({ visits, footer }: Props) => {
  const [dropdownOpen, setDropdownOpen] = useState<{ [key: string]: boolean }>(
    {},
  );
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cancellationReasons, setCancellationReasons] = useState<string[]>([]);
  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(null);

  const toggleDropdown = (visitId: string) => {
    setDropdownOpen((prev) => ({
      ...prev,
      [visitId]: !prev[visitId],
    }));
  };

  const { callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();

  const navigate = useNavigate();

  const doVisitDone = useCallback(
    async (visitId: string) => {
      const { status } = await callEndpoint(
        markAsDoneVisitPreSaleEndpoint(visitId, token),
      );
      if (status === 200) navigate(0);
    },
    [callEndpoint, token],
  );

  const doCancelVisit = useCallback(
    async (visitId: string) => {
      const { status } = await callEndpoint(
        deleteVisitPreSaleEndpoint(visitId, token),
      );
      if (status === 200) navigate(0);
    },
    [callEndpoint, token],
  );

  const handleDeleteVisit = async (
    reason: string,
    explain_reason: string,
    visitStatus: string,
  ) => {
    if (!selectedVisitId) return;
    const { status } = await callEndpoint(
      deleteVisitPreSaleEndpoint(
        selectedVisitId,
        token,
        reason,
        explain_reason,
        visitStatus,
      ),
    );
    if (status === 200) navigate(0);
    setDeleteModalOpen(false);
    setSelectedVisitId(null);
  };

  const handleOpenDeleteModal = async (visitId: string) => {
    setSelectedVisitId(visitId);
    try {
      const { data } = await callEndpoint(getCancellationReasons(token));
      setCancellationReasons(Array.isArray(data.data) ? data.data : []);
    } catch {
      setCancellationReasons([]);
    }
    setDeleteModalOpen(true);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Asesorias</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="table-responsive">
            <Table striped hover>
              <thead>
                <tr>
                  <th>Proyecto</th>
                  <th>Inversionista</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {visits.map((visit) => (
                  <tr key={visit.id}>
                    <td>
                      <div>
                        <div className="font-weight-bold">
                          [{visit.projectInfo.code}] {visit.projectInfo.name}
                        </div>
                        <div className="d-flex align-items-center text-muted small">
                          <IconMapPin size={12} className="me-1" />
                          {visit.projectInfo.address}
                        </div>
                        <div className="text-success font-weight-bold">
                          {visit.projectInfo.range_price}
                        </div>
                        <VisitMarketplaceTypeBadge
                          marketplace_type={visit.visitInfo.marketplace_type}
                        />
                      </div>
                    </td>
                    <td>
                      <div>
                        <div className="font-weight-bold">
                          {visit.visitInfo.investor_name}
                        </div>
                        <div className="d-flex align-items-center text-muted">
                          <IconMail size={12} className="me-1" />{' '}
                          {visit.visitInfo.investor_email}
                        </div>
                        <div className="d-flex align-items-center text-muted">
                          <IconPhone size={12} className="me-1" />
                          {visit.visitInfo.investor_phone}
                        </div>
                        <div className="d-flex align-items-center font-weight-bold">
                          <IconCalendar size={12} className="me-1" />
                          {visit.visitInfo.visit_date}
                        </div>
                        <div className="d-flex align-items-center font-weight-bold">
                          <IconClock size={12} className="me-1" />
                          {visit.visitInfo.visit_time}
                        </div>
                      </div>
                    </td>
                    <td>
                      <InvestorVisitsBadge
                        status={visit.visitInfo.status}
                        className="badge-lg mr-2"
                      />
                    </td>
                    <td>
                      <Dropdown
                        isOpen={dropdownOpen[visit.id] || false}
                        toggle={() => toggleDropdown(visit.id)}
                      >
                        <DropdownToggle
                          tag="button"
                          className="btn btn-sm btn-outline-secondary"
                          caret={false}
                        >
                          <IconDots size={16} />
                        </DropdownToggle>
                        <DropdownMenu>
                          {[
                            VisitPreSaleState.SCHEDULED,
                            VisitPreSaleState.RESCHEDULED,
                          ].includes(visit.visitInfo.status) && (
                            <DropdownItem
                              className="text-success"
                              onClick={() => doVisitDone(visit.id)}
                            >
                              <IconCheck size={16} className="me-2" />
                              Marcar como realizada
                            </DropdownItem>
                          )}

                          <DropdownItem
                            onClick={() => {
                              navigate(
                                `/investors/projects/${visit.projectInfo.id}/visits`,
                              );
                            }}
                          >
                            <IconCalendar size={16} className="me-2" />
                            Ver en calendario
                          </DropdownItem>
                          {[
                            VisitPreSaleState.SCHEDULED,
                            VisitPreSaleState.RESCHEDULED,
                          ].includes(visit.visitInfo.status) && (
                            <>
                              <DropdownItem divider />
                              <DropdownItem
                                className="text-danger"
                                onClick={() => handleOpenDeleteModal(visit.id)}
                              >
                                <IconTrash size={16} className="me-2" />
                                Cancelar visita
                              </DropdownItem>
                            </>
                          )}
                        </DropdownMenu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
        <CardFooter>{footer && <div>{footer}</div>}</CardFooter>
      </Card>
      <DeleteVisitModal
        isOpen={deleteModalOpen}
        toggle={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteVisit}
        reasons={cancellationReasons}
      />
    </>
  );
};

export default DashboardPreSaleVisitListTable;
